import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Layout
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';

// Auth
import LoginPage from './pages/Auth/LoginPage';
import { useAuth } from './hooks/useAuth';

// Public
import LandingPage from './pages/Public/LandingPage';
import SignupPage from './pages/Public/SignupPage';

// Legal
import PrivacyPage from './pages/Legal/PrivacyPage';
import TermsPage from './pages/Legal/TermsPage';

// Dashboard pages
import DashboardHomePage from './pages/Dashboard/DashboardHomePage';
import MemoriesPage from './pages/Dashboard/MemoriesPage';
import PlaygroundPage from './pages/Dashboard/PlaygroundPage';
import TagsPage from './pages/Dashboard/TagsPage';
import UsagePage from './pages/Dashboard/UsagePage';
import ApiKeyPage from './pages/Dashboard/ApiKeyPage';
import AdminPage from './pages/Dashboard/AdminPage';

// Documentation
import DocumentationViewer from './components/DocumentationViewer';

// Dashboard visual shell (tokens, sidebar, topbar, panels, etc.)
// Scoped under `.mme-dashboard` so tokens don't leak into Tailwind
// utilities used elsewhere (login page, docs viewer, landing).
import './pages/Dashboard/DashboardShell.css';

const PUBLIC_PATHS = ['/', '/signup', '/privacy', '/terms'];

// Routes that render bare (no dashboard chrome), regardless of auth state.
// DocumentationViewer ships its own nav + sidebar — wrapping it in the
// `.mme-dashboard > .dash` shell produced a double-chrome bug (two sidebars,
// two top-nav regions). This list keeps it chromeless everywhere.
const BARE_PATHS = ['/documentation'];

// Keyboard shortcut map — mirrors the sidebar labels.
const SHORTCUT_TO_PATH = {
  h: '/dashboard',
  m: '/memories',
  p: '/playground',
  t: '/tags',
  u: '/usage',
  k: '/api-key',
  a: '/admin',
};

function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close sidebar on route change (mobile drawer).
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Global single-key shortcuts — h / m / u / k / a. Skip when the user is
  // typing in an input/textarea/contenteditable or holding a modifier,
  // otherwise the search box would hijack every letter the user types.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (t.isContentEditable) return;
      const key = (e.key || '').toLowerCase();
      const path = SHORTCUT_TO_PATH[key];
      if (!path) return;
      e.preventDefault();
      navigate(path);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAuthenticated, navigate]);

  // Bare routes — always rendered chromeless (see BARE_PATHS comment).
  const isBarePath = BARE_PATHS.includes(location.pathname);
  if (isBarePath) {
    return (
      <Routes>
        <Route path="/documentation" element={<DocumentationViewer />} />
      </Routes>
    );
  }

  // Public routes
  const isPublicPath = PUBLIC_PATHS.includes(location.pathname);

  if (isPublicPath && !isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FAFAF7]">
        <div className="animate-spin w-6 h-6 border-2 border-[#0A0A0A] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Not authenticated → login
  if (!isAuthenticated) {
    return (
      <>
        <Helmet><title>Sign in - MME</title></Helmet>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </>
    );
  }

  // Authenticated dashboard — wrapped in `.mme-dashboard` which owns
  // the design tokens and the `.dash` 240px + 1fr grid.
  return (
    <>
      <Helmet><title>MME</title></Helmet>

      <div className="mme-dashboard">
        <div className="dash">
          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

          <div className="main">
            <Header onMenuClick={() => setSidebarOpen(true)} />

            <Routes>
              {/* Dashboard pages */}
              <Route path="/dashboard" element={<DashboardHomePage />} />
              <Route path="/memories" element={<MemoriesPage />} />
              <Route path="/playground" element={<PlaygroundPage />} />
              <Route path="/tags" element={<TagsPage />} />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/api-key" element={<ApiKeyPage />} />
              <Route path="/admin" element={<AdminPage />} />

              {/* Legal pages (accessible when authenticated too) */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/* /documentation is intentionally absent — see BARE_PATHS above. */}

              {/* Redirects: old routes + root → dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/run" element={<Navigate to="/dashboard" replace />} />
              <Route path="/traces" element={<Navigate to="/dashboard" replace />} />
              <Route path="/seeds" element={<Navigate to="/dashboard" replace />} />
              <Route path="/memory" element={<Navigate to="/memories" replace />} />
              <Route path="/whitepaper" element={<Navigate to="/dashboard" replace />} />
              <Route path="/quick-start" element={<Navigate to="/api-key" replace />} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
